import { useSyncExternalStore } from "react";

/** What a person can pick. `rigg-auto` follows the machine between the two rigg modes. */
export const THEMES = [
  { id: "onyxia", label: "Onyxia" },
  { id: "rigg-auto", label: "Rigg · auto" },
  { id: "rigg-light", label: "Rigg · lys" },
  { id: "rigg-dark", label: "Rigg · mørk" },
] as const;
export type ThemeId = (typeof THEMES)[number]["id"];
/** What lands on `<html data-theme>`. The default sets no attribute at all. */
export type ResolvedTheme = Exclude<ThemeId, "rigg-auto">;

const DEFAULT_THEME: ThemeId = "onyxia";
const STORAGE_KEY = "backoffice_theme";
const FONT_LINK_ID = "theme-fonts";
const RIGG_FONTS_HREF = "/fonts/rigg.css";

export function parseTheme(raw: string | null | undefined): ThemeId {
  return THEMES.some((t) => t.id === raw) ? (raw as ThemeId) : DEFAULT_THEME;
}

export function resolveTheme(id: ThemeId, prefersDark: boolean): ResolvedTheme {
  if (id === "rigg-auto") return prefersDark ? "rigg-dark" : "rigg-light";
  return id;
}

let darkMql: MediaQueryList | undefined;
const darkQuery = () => (darkMql ??= window.matchMedia("(prefers-color-scheme: dark)"));

function readStored(): ThemeId {
  try {
    return parseTheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

// What is on the document right now. useTheme reports this rather than
// re-reading storage, so the picker matches the page even when the browser
// refused the write.
let current: ThemeId = DEFAULT_THEME;

function apply(id: ThemeId) {
  current = id;
  const root = document.documentElement;
  const resolved = resolveTheme(id, darkQuery().matches);
  if (resolved === "onyxia") {
    root.removeAttribute("data-theme");
    return;
  }
  root.setAttribute("data-theme", resolved);
  // Linked on first use, so the default theme never requests the faces.
  if (!document.getElementById(FONT_LINK_ID)) {
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = RIGG_FONTS_HREF;
    document.head.appendChild(link);
  }
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => current;

declare global {
  // Kept on globalThis rather than in this module so it survives an HMR
  // re-evaluation, which would otherwise stack a second set of listeners.
  var __backofficeThemeInit: AbortController | undefined;
}

/** Call at startup, before the first render. Calling it again replaces the previous listeners. */
export function initTheme() {
  globalThis.__backofficeThemeInit?.abort();
  const ac = new AbortController();
  globalThis.__backofficeThemeInit = ac;
  apply(readStored());
  darkQuery().addEventListener("change", () => apply(readStored()), { signal: ac.signal });
  window.addEventListener(
    "storage",
    (e) => {
      // key is null when another tab cleared storage wholesale.
      if (e.key !== null && e.key !== STORAGE_KEY) return;
      apply(readStored());
      notify();
    },
    { signal: ac.signal },
  );
}

export function setTheme(id: ThemeId) {
  try {
    // An absent key is the default, so someone who never opened the control has nothing written down.
    if (id === DEFAULT_THEME) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, id);
  } catch {}
  apply(id);
  notify();
}

export function useTheme(): [ThemeId, (id: ThemeId) => void] {
  const theme = useSyncExternalStore(subscribe, getSnapshot);
  return [theme, setTheme];
}
